import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import { addStudentApi, updateStudentApi, fetchAcademicYearApi } from "../../../../core/data/services/studentService";
import { fetchclassesApi } from "../../../../core/data/services/classService";
import { classes } from "../../../../core/data/types/class";
import { } from "../../../../core/data/services/sectionService";
import { addStudentSuccess } from "../../../../core/data/redux/studentSlice";
import { Student } from "../../../../core/data/types/student";
import {
  bloodGroup, casteCategory, gender, motherTongue, religion, status, aadharStatus,
} from "../../../../core/common/selectoption/selectoption";
import { studentinputs } from "../../../../core/common/inputoption/inputoption";
import CommonSelect from "../../../../core/common/commonSelect";
import CustomInput from "../../../../core/common/commonInput";
import CustomCheckbox from "../../../../core/common/commonCheckbox";
import CustomTextArea from "../../../../core/common/commonTextArea";
import CustomDate from "../../../../core/common/commonDate";
// import CustomFileInput from "../../../../core/common/CustomFileInput";
import CustomMail from "../../../../core/common/commonMail";
import { all_routes } from "../../../router/all_routes";
import CustomRadio from "../../../../core/common/commonRadio";
import ErrorModal from "../../../../core/common/ErrorModal";
import SuccessModal from "../../../../core/common/successModal";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { REACT_APP_AWS_REGION, REACT_APP_AWS_ACCESS_KEY_ID, REACT_APP_AWS_SECRET_ACCESS_KEY, REACT_APP_AWS_S3_BUCKET_NAME } from "../../../../constants/constants"
import { getUserDetails } from "../../../../lib/getUserDetails";
import WebcamCapture from "../../../../core/common/WebcamCapture";

type Option = { label: string; value: string };

interface AcademicYear {
  id: number;
  academicYear: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

interface AcademicYearResponse {
  statusCode: number;
  status: string;
  message: string;
  total: number;
  data: AcademicYear[];
}

const optionsMap: Record<string, Option[]> = {
  bloodGroup, aadharStatus, casteCategory, gender, motherTongue, religion, status,
};

const s3 = new S3Client({
  region: REACT_APP_AWS_REGION || "us-east-1",
  credentials: {
    accessKeyId: REACT_APP_AWS_ACCESS_KEY_ID || "YOUR_AWS_ACCESS_KEY_ID",
    secretAccessKey: REACT_APP_AWS_SECRET_ACCESS_KEY || "YOUR_AWS_SECRET_ACCESS_KEY",
  },
});

async function uploadFileToS3(file: File): Promise<string> {
  const maxSize = 4 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error("File size exceeds 4MB limit");
  }

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Invalid file type. Only JPG, PNG, and SVG are allowed");
  }

  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  const fileName = `uploads/${ timestamp }_${ randomString }.${ fileExtension }`;

  try {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const command = new PutObjectCommand({
      Bucket: REACT_APP_AWS_S3_BUCKET_NAME,
      Key: fileName,
      Body: uint8Array,
      ContentType: file.type,
    });

    await s3.send(command);
    const s3Url = `https://${ REACT_APP_AWS_S3_BUCKET_NAME }.s3.${ REACT_APP_AWS_REGION }.amazonaws.com/${ fileName }`;
    return s3Url;
  } catch (error) {
    console.error("S3 upload error:", error);

    if (error instanceof Error) {
      if (error.message.includes('AccessDenied')) {
        throw new Error("Access denied. Check your AWS credentials and permissions.");
      } else if (error.message.includes('NoSuchBucket')) {
        throw new Error("S3 bucket not found. Check your bucket name.");
      } else {
        throw new Error(`Upload failed: ${ error.message }`);
      }
    }

    throw new Error("Unknown error occurred during upload");
  }
}

const getUserSchoolId = (): number => {
  try {
    const userData = getUserDetails();

    if (userData && userData.length > 0 && userData[0]?.relatedEntity?.schools?.id) {
      const schoolId = userData[0].relatedEntity.schools.id;
      return schoolId;
    } else {
      console.error("Could not extract schoolId from userData:", userData);
      return 0;
    }
  } catch (error) {
    console.error("Error getting school ID:", error);
    return 0;
  }
};

const getDefaultFormData = (): Omit<Student, "id"> & { physicalChallengeDetails?: string | null } => {
  const schoolId = getUserSchoolId();

  return {
    academicYearId: "",
    firstName: "",
    lastName: "",
    dob: null,
    gender: "",
    bloodGroup: "",
    religion: "",
    casteCategory: "",
    motherTongue: "",
    rollNo: "",
    email: "",
    admissionDate: null,
    schoolClassId: null,
    classSectionId: null,
    schoolId: schoolId || 0,
    address: null,
    fatherName: null,
    motherName: null,
    primaryContactNo: null,
    secondaryContactNo: null,
    aadharNumber: null,
    aadharStatus: '',
    physicallyChallenged: false,
    physicalChallengeDetails: null,
    mdmEnrolled: false,
    caste: null,
    fatherAadhar: null,
    motherAadhar: null,
    profileImage: null,
  };
};

const AddStudent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const isEdit = Boolean(state?.student);
  const studentFromState = state?.student as Student | undefined;

  const [formData, setFormData] = useState<Omit<Student, "id"> & { physicalChallengeDetails?: string | null }>(getDefaultFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [classesData, setClassesData] = useState<classes[]>([]);
  const [classOptions, setClassOptions] = useState<Option[]>([]);
  const [sectionOptions, setSectionOptions] = useState<Option[]>([]);
  const [loadingClasses, setLoadingClasses] = useState<boolean>(false);

  const [, setAcademicYearsData] = useState<AcademicYear[]>([]);
  const [academicYearOptions, setAcademicYearOptions] = useState<Option[]>([]);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState<boolean>(false);

  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [imageUploading, setImageUploading] = useState<boolean>(false);

  const [showCaptureModal, setShowCaptureModal] = useState(false);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        setLoadingAcademicYears(true);
        const response: AcademicYearResponse = await fetchAcademicYearApi();

        if (response.status === "success" && response.data) {
          setAcademicYearsData(response.data);

          const options: Option[] = response.data.map(yearItem => {
            const startDate = new Date(yearItem.startDate);
            const monthName = startDate.toLocaleString('default', { month: 'long' });

            return {
              label: `${ monthName } ${ yearItem.academicYear }`,
              value: yearItem.id.toString()
            };
          });
          setAcademicYearOptions(options);
        }
      } catch (error) {
        console.error("Error fetching academic years:", error);
        setErrorMessage("Failed to load academic years. Please refresh the page.");
        setShowErrorModal(true);
      } finally {
        setLoadingAcademicYears(false);
      }
    };

    fetchAcademicYears();
  }, []);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const schoolId = getUserSchoolId();
        if (!schoolId || schoolId === 0) {
          return;
        }

        setLoadingClasses(true);
        const response: classes[] = await fetchclassesApi();

        if (response && response.length > 0) {
          setClassesData(response);

          const options: Option[] = response.map(classItem => ({
            label: classItem.name,
            value: classItem.id.toString()
          }));
          setClassOptions(options);
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
        setErrorMessage("Failed to load classes. Please refresh the page.");
        setShowErrorModal(true);
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClasses();
  }, []);

  const handleClassChange = (selectedClass: Option | null) => {
    const classId = selectedClass?.value;

    handleInputChange('schoolClassId', classId ? Number(classId) : null);
    handleInputChange('classSectionId', null);

    if (classId) {
      const selectedClassData = classesData.find(cls => cls.id.toString() === classId);
      if (selectedClassData && selectedClassData.sectionsArr && selectedClassData.sectionsIdArr) {
        const sectionOpts: Option[] = selectedClassData.sectionsArr.map((sectionName: string, index: number) => ({
          label: sectionName.toUpperCase(),
          value: selectedClassData.sectionsIdArr[index].toString()
        }));
        setSectionOptions(sectionOpts);
      } else {
        setSectionOptions([]);
      }
    } else {
      setSectionOptions([]);
    }
  };

  const handleSectionChange = (selectedSection: Option | null) => {
    handleInputChange('classSectionId', selectedSection?.value ? Number(selectedSection.value) : null);
  };

  useEffect(() => {
    if (isEdit && studentFromState) {
      const { id, ...rest } = studentFromState;
      const compatibleRest = {
        ...rest,
        physicalChallengeDetails: rest.physicalChallengeDetails ?? null
      };

      setFormData(compatibleRest);

      if (rest.schoolClassId && classesData.length > 0) {
        const selectedClassData = classesData.find(cls => cls.id === Number(rest.schoolClassId));
        if (selectedClassData && selectedClassData.sectionsArr && selectedClassData.sectionsIdArr) {
          const sectionOpts: Option[] = selectedClassData.sectionsArr.map((sectionName: string, index: number) => ({
            label: sectionName.toUpperCase(),
            value: selectedClassData.sectionsIdArr[index].toString()
          }));
          setSectionOptions(sectionOpts);
        }
      }
    }
  }, [isEdit, studentFromState, classesData]);

  const numberFields = ["academicYearId", "schoolClassId", "classSectionId"];

  const validateField = (key: keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }), value: any): string => {
    const mandatoryFields = getMandatoryFields();
    const isMandatory = mandatoryFields.includes(key);

    if (key === "rollNo" && isEdit && studentFromState?.rollNo) {
      if (value && value.toString().trim().length > 0 && value.toString().trim().length < 3) {
        return "Roll number must be at least 3 characters long";
      }
      return "";
    }

    if (isMandatory && (value === "" || value === null || value === undefined)) {
      return "This field is required";
    }

    if (!isMandatory && (value === "" || value === null || value === undefined)) {
      return "";
    }

    switch (key) {
      case "firstName":
      case "fatherName":
      case "motherName":
        if (value && value.trim().length < 3) {
          return "Name must be at least 3 characters long";
        }
        break;

      case "email":
        if (value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return "Please enter a valid email address";
          }
        }
        break;

      case "aadharNumber":
      case "fatherAadhar":
      case "motherAadhar":
        if (value) {
          const cleanAadhar = value.replace(/[^\d]/g, '');
          if (cleanAadhar.length !== 12) {
            return "Aadhar number must be exactly 12 digits";
          }
          if (!/^\d{12}$/.test(cleanAadhar)) {
            return "Aadhar number must contain only digits";
          }
        }
        break;

      case "primaryContactNo":
        if (value) {
          const cleanPhone = value.replace(/[^\d]/g, '');
          if (cleanPhone.length !== 10) {
            return "Mobile number must be exactly 10 digits";
          }
          if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
            return "Please enter a valid Indian mobile number";
          }
        }
        break;

      case "secondaryContactNo":
        if (value && value.trim() !== '') {
          const cleanPhone = value.replace(/[^\d]/g, '');
          if (cleanPhone.length !== 10) {
            return "Mobile number must be exactly 10 digits";
          }
          if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
            return "Please enter a valid Indian mobile number";
          }
        }
        break;

      case "dob":
        if (value) {
          let dobDate: Date;
          if (typeof value === 'string') {
            dobDate = new Date(value);
          } else if (value instanceof Date) {
            dobDate = value;
          } else {
            return "Please enter a valid date";
          }

          if (isNaN(dobDate.getTime())) {
            return "Please enter a valid date";
          }

          const today = new Date();
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(today.getFullYear() - 1);

          if (dobDate > today) {
            return "Date of birth cannot be in the future";
          }
          if (dobDate > oneYearAgo) {
            return "Student must be at least 1 year old";
          }

          const twentyFiveYearsAgo = new Date();
          twentyFiveYearsAgo.setFullYear(today.getFullYear() - 25);
          if (dobDate < twentyFiveYearsAgo) {
            return "Please check the date of birth";
          }
        }
        break;

      case "admissionDate":
        if (value && value !== '') {
          let admissionDate: Date;
          if (typeof value === 'string') {
            admissionDate = new Date(value);
          } else if (value instanceof Date) {
            admissionDate = value;
          } else {
            return "Please enter a valid date";
          }

          if (isNaN(admissionDate.getTime())) {
            return "Please enter a valid date";
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          admissionDate.setHours(0, 0, 0, 0);

          // For edit mode, check if admission date has been changed
          if (isEdit && studentFromState?.admissionDate) {
            let originalAdmissionDate: Date;
            if (typeof studentFromState.admissionDate === 'string') {
              originalAdmissionDate = new Date(studentFromState.admissionDate);
            } else {
              originalAdmissionDate = new Date(studentFromState.admissionDate);
            }
            originalAdmissionDate.setHours(0, 0, 0, 0);

            // If date hasn't changed, skip the "one week ago" validation
            if (admissionDate.getTime() === originalAdmissionDate.getTime()) {
              // Only validate future date for unchanged dates
              if (admissionDate > today) {
                return "Date cannot be in the Future";
              }

              // Skip the "one week ago" validation for unchanged dates
              if (formData.dob) {
                let dobDate: Date;
                if (typeof formData.dob === 'string') {
                  dobDate = new Date(formData.dob);
                } else {
                  dobDate = new Date(formData.dob);
                }
                if (admissionDate < dobDate) {
                  return "Admission date cannot be before date of birth";
                }
              }
              break; // Exit validation early for unchanged dates
            }
          }

          // Apply full validation for new students or changed dates
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(today.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);

          if (admissionDate < oneWeekAgo) {
            return "date cannot be more than one week in the past";
          }

          if (admissionDate > today) {
            return "Date cannot be in the Future";
          }

          if (formData.dob) {
            let dobDate: Date;
            if (typeof formData.dob === 'string') {
              dobDate = new Date(formData.dob);
            } else {
              dobDate = new Date(formData.dob);
            }

            if (admissionDate < dobDate) {
              return "Admission date cannot be before date of birth";
            }
          }
        }
        break;

      case "rollNo":
        if (value && value.toString().trim().length < 3) {
          return "Roll number must be at least 3 characters long";
        }
        break;

      case "physicalChallengeDetails":
        if (formData.physicallyChallenged === true && (!value || value.trim().length === 0)) {
          return "Please specify the physical challenge details";
        }
        if (value && value.trim().length < 3) {
          return "Physical challenge details must be at least 3 characters long";
        }
        break;

      default:
        break;
    }

    return "";
  };

  const handleInputChange = (key: keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }), value: any) => {
    let processedValue = value;

    if (numberFields.includes(key as string) && value !== "" && value !== null) {
      processedValue = Number(value);
    }

    if (['primaryContactNo', 'secondaryContactNo'].includes(key as string) && value) {
      processedValue = value.replace(/[^\d]/g, '').slice(0, 10);
    }

    if (['aadharNumber', 'fatherAadhar', 'motherAadhar'].includes(key as string) && value) {
      processedValue = value.replace(/[^\d]/g, '').slice(0, 12);
    }

    setFormData(prev => ({ ...prev, [key]: processedValue }));

    const errorMessage = validateField(key, processedValue);
    setErrors(prev => ({
      ...prev,
      [key]: errorMessage
    }));
  };

  const handleSelectChange = (key: keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null })) => (selected: Option | null) =>
    handleInputChange(key, selected?.value ?? "");

  const getMandatoryFields = (): (keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }))[] => {
    const mandatoryKeys: string[] = Object.values(studentinputs[0]).flatMap((section: any) =>
      section.filter((field: any) => field.mandatory).map((field: any) => field.key)
    );

    const optionalFields = ['caste', 'secondaryContactNo', 'admissionDate']; // Added admissionDate to optional fields

    if (isEdit && formData.rollNo && formData.rollNo.trim() !== '') {
      optionalFields.push('rollNo');
    }

    const filteredMandatoryKeys = mandatoryKeys.filter(key => !optionalFields.includes(key));

    if (formData.physicallyChallenged === true) {
      filteredMandatoryKeys.push('physicalChallengeDetails');
    }

    return filteredMandatoryKeys as (keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }))[];
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    Object.keys(formData).forEach(key => {
      const fieldKey = key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null });
      const errorMessage = validateField(fieldKey, formData[fieldKey]);
      if (errorMessage) {
        newErrors[key] = errorMessage;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const cleanFormDataForAPI = (data: Omit<Student, "id"> & { physicalChallengeDetails?: string | null }): Omit<Student, "id"> => {
    const cleanedData = { ...data };
    const { physicalChallengeDetails, ...studentData } = cleanedData;

    Object.keys(studentData).forEach(key => {
      const value = studentData[key as keyof Omit<Student, "id">];

      if (key === 'rollNo') {
        studentData.rollNo = String(value || '');
        return;
      }

      if (key === 'dob' || key === 'admissionDate') {
        if (value === '' || value === null || value === undefined) {
          (studentData as any)[key] = null;
        } else if (typeof value === 'string' && value.trim() !== '') {
          const dateStr = value.includes('T') ? value : value + 'T00:00:00.000Z';
          (studentData as any)[key] = new Date(dateStr);
        } else if (value instanceof Date) {
          (studentData as any)[key] = value;
        }
        return;
      }

      if (typeof value === 'string' && value.trim() === '') {
        if (!['firstName', 'lastName', 'email'].includes(key)) {
          (studentData as any)[key] = null;
        }
      }

      if (numberFields.includes(key) && (value === '' || value === null)) {
        (studentData as any)[key] = null;
      }
    });

    const schoolId = getUserSchoolId();
    if (schoolId > 0) {
      studentData.schoolId = schoolId;
    }

    if (data.physicallyChallenged === true && physicalChallengeDetails) {
      (studentData as any).physicalChallengeDetails = physicalChallengeDetails;
    }

    return studentData;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isEdit && studentFromState) {
        const changedFields: any = { id: studentFromState.id };
        let anyChange = false;

        const cleanedFormData = cleanFormDataForAPI(formData);

        for (const key in cleanedFormData) {
          const k = key as keyof Omit<Student, "id">;
          let currentValue = cleanedFormData[k];
          let originalValue = (studentFromState as any)[k];

          if (k === 'schoolId') {
            continue;
          }

          if (k === 'rollNo') {
            currentValue = String(currentValue || '').trim();
            originalValue = String(originalValue || '').trim();
            if (currentValue === originalValue) {
              continue;
            }
          }

          if (k === 'admissionDate' || k === 'dob') {
            let currentDateStr = '';
            let originalDateStr = '';

            if (currentValue instanceof Date) {
              currentDateStr = currentValue.toISOString().split('T')[0];
            } else if (typeof currentValue === 'string' && currentValue) {
              currentDateStr = currentValue;
              currentValue = new Date(currentValue + 'T00:00:00.000Z');
            }

            if (originalValue instanceof Date) {
              originalDateStr = originalValue.toISOString().split('T')[0];
            } else if (typeof originalValue === 'string' && originalValue) {
              originalDateStr = originalValue;
            }

            if (currentDateStr === originalDateStr) {
              continue;
            }

            changedFields[k] = currentValue;
            anyChange = true;
            continue;
          }

          const isChanged = JSON.stringify(currentValue) !== JSON.stringify(originalValue);
          if (isChanged) {
            changedFields[k] = currentValue;
            anyChange = true;
          }
        }

        if (!anyChange) {
          setSuccessMessage("No changes detected.");
          setShowSuccessModal(true);
        } else {
          const updated = await updateStudentApi(changedFields);
          dispatch(addStudentSuccess(updated));
          setSuccessMessage("Successfully updated!");
          setShowSuccessModal(true);
          navigate(all_routes.studentGrid);
        }
      } else {
        const cleanedData = cleanFormDataForAPI(formData);
        const added = await addStudentApi(cleanedData);
        dispatch(addStudentSuccess(added));
        setSuccessMessage("Student added successfully!");
        setShowSuccessModal(true);
        navigate(all_routes.studentGrid);
        setFormData(getDefaultFormData());
      }
      setErrors({});
    } catch (err: any) {
      console.error("Submission Error:", err);
      console.error("Error response:", err.response?.data);

      let msg = "Something went wrong. Please try again.";

      if (err.response?.data?.message) {
        if (Array.isArray(err.response.data.message)) {
          msg = err.response.data.message.join(', ');
        } else {
          msg = err.response.data.message;
        }
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err.response?.data?.details) {
        msg = err.response.data.details;
      } else if (err.message) {
        msg = err.message;
      }

      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const newErrors: Record<string, string> = {};

        if (Array.isArray(apiErrors)) {
          apiErrors.forEach((error: any) => {
            if (error.field) {
              newErrors[error.field] = error.message || "Invalid value";
            }
          });
          setErrors(newErrors);
        }

        msg = "Please check the highlighted fields and try again.";
      }

      setErrorMessage(msg);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };

  const getDynamicOptionsMap = (): Record<string, Option[]> => {
    const baseClassOptions = loadingClasses
      ? [{ label: "Loading classes...", value: "" }]
      : classOptions.length > 0
        ? [{ label: "Select Class", value: "" }, ...classOptions]
        : [{ label: "No classes available", value: "" }];

    const baseSectionOptions = !formData.schoolClassId
      ? [{ label: "Select class first", value: "" }]
      : sectionOptions.length > 0
        ? [{ label: "Select Section", value: "" }, ...sectionOptions]
        : [{ label: "No sections available", value: "" }];

    const baseAcademicYearOptions = loadingAcademicYears
      ? [{ label: "Loading academic years...", value: "" }]
      : academicYearOptions.length > 0
        ? [{ label: "Select Academic Year", value: "" }, ...academicYearOptions]
        : [{ label: "No academic years available", value: "" }];

    return {
      ...optionsMap,
      academicYearId: baseAcademicYearOptions,
      schoolClassId: baseClassOptions,
      classSectionId: baseSectionOptions,
    };
  };

  const dynamicOptionsMap = getDynamicOptionsMap();

  const userRole = useMemo(() => {
    const userData = getUserDetails();
    return userData?.[0]?.role;
  }, []);

  console.log("userRole", userRole);
  const getDashboardRoute = (roleRaw: string) => {
    const role = roleRaw.toLowerCase().trim();

    const direct: Record<string, keyof typeof all_routes> = {
      teacher: "teacherDashboard",
      headmaster: "HeadmasterDashboard",

    };

    if (role in direct) return all_routes[direct[role]];

    if (role.includes("teacher")) return all_routes.teacherDashboard;
    if (role.includes("headmaster") || role.includes("hm")) return all_routes.HeadmasterDashboard;
    const fallback: keyof typeof all_routes = "teacherDashboard";
    return all_routes[fallback] ?? "/";
  };


  const dashboardLink = useMemo(() => getDashboardRoute(userRole), [userRole]);

  const groupedClassSections = useMemo(() => {
    if (userRole !== 'Teacher') return {};

    try {
      const userData = getUserDetails();
      const userInfo = userData?.[0];
      const schoolClasses = userInfo?.relatedEntity?.schoolClasses || [];

      const sections = schoolClasses.reduce((acc: any[], schoolClassObj: any) => {
        if (Array.isArray(schoolClassObj.classSection)) {
          const className = schoolClassObj.schoolClass?.name || schoolClassObj.name;
          const classId = schoolClassObj.schoolClass?.id || schoolClassObj.id;
          const sections = schoolClassObj.classSection.map((section: any) => ({
            classId,
            className,
            sectionName: section?.name,
            sectionId: section?.id,
          }));
          return [...acc, ...sections];
        }
        return acc;
      }, []);

      return sections.reduce((grouped: any, section: any) => {
        const { className } = section;
        if (!grouped[className]) {
          grouped[className] = [];
        }
        grouped[className].push(section);
        return grouped;
      }, {});
    } catch (error) {
      console.error('Error extracting class sections:', error);
      return {};
    }
  }, [userRole]);

  const classNames = useMemo(() => Object.keys(groupedClassSections), [groupedClassSections]);

  const selectedClassName = useMemo(() => {
    return classNames.find(name =>
      groupedClassSections[name]?.some(
        (sec: any) => sec.classId === Number(formData.schoolClassId)
      )
    );
  }, [formData.schoolClassId, classNames, groupedClassSections]);

  const sectionsForSelectedClass = useMemo(() => {
    if (!selectedClassName) return [];
    return groupedClassSections[selectedClassName] || [];
  }, [selectedClassName, groupedClassSections]);

  // Helper function to render form field
  const renderFormField = ({ label, type, key, mandatory }: any) => {
    return (
      <>
        <label className="form-label">
          {label}
          {mandatory && !['caste', 'secondaryContactNo', 'admissionDate'].includes(key) && <span style={{ color: "red" }}> *</span>}
        </label>

        {key === "faceImage" ? (
          <>
            <div className="profile-upload">
              {/* Display current image if exists */}
              {formData.faceImage && (
                <img
                  src={formData.faceImage}
                  alt="Face Preview"
                  style={{ width: 100, height: 100, objectFit: "cover", marginBottom: 10 }}
                />
              )}

              {imageUploading && <p>Uploading...</p>}

              {/* File Upload Option */}
              <div className="upload-option mb-3">
                <label htmlFor="faceImageUpload" className="btn btn-primary me-2">
                  <i className="fas fa-upload me-2"></i>
                  {/* Upload Image */}
                </label>
                <input
                  id="faceImageUpload"
                  type="file"
                  accept="image/jpeg,image/jpg"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Validate file size (4MB limit)
                      if (file.size > 4 * 1024 * 1024) {
                        setErrorMessage("File size must be under 4MB");
                        setShowErrorModal(true);
                        return;
                      }

                      // Validate file type
                      if (!file.type.includes('jpeg') && !file.type.includes('jpg')) {
                        setErrorMessage("Only JPG format is allowed");
                        setShowErrorModal(true);
                        return;
                      }

                      try {
                        setImageUploading(true);
                        const s3Url = await uploadFileToS3(file);
                        handleInputChange("faceImage", s3Url);
                      } catch (err: any) {
                        console.error("Face image upload failed:", err);
                        setErrorMessage(`Face image upload failed: ${ err.message }`);
                        setShowErrorModal(true);
                      } finally {
                        setImageUploading(false);
                      }
                    }
                  }}
                />

                {/* Camera Capture Button */}
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => setShowCaptureModal(true)}
                >
                  <i className="fas fa-camera me-2"></i>
                  {/* Capture Image */}
                </button>
                {/* Remove Button */}
                {formData.faceImage && (
                  <Link
                    to="#"
                    className="btn btn-danger ms-2"
                    onClick={(e) => {
                      e.preventDefault();
                      handleInputChange(
                        "faceImage" as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                        null
                      );
                    }}
                  >
                    <i className="fas fa-trash me-2"></i>
                    {/* Remove */}
                  </Link>
                )}
              </div>




              <p className="fs-12 mt-2 text-muted">
                Upload or capture image • Size under 4MB • JPG format only
              </p>
            </div>

            {/* Camera Capture Modal */}
            {showCaptureModal && (
              <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="fas fa-camera me-2"></i>
                        Capture Face Image
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowCaptureModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body text-center">
                      <WebcamCapture
                        onCapture={async (file) => {
                          try {
                            setImageUploading(true);
                            setShowCaptureModal(false); // Close modal immediately after capture
                            const s3Url = await uploadFileToS3(file);
                            handleInputChange("faceImage", s3Url);
                          } catch (err: any) {
                            console.error("Face image upload failed:", err);
                            setErrorMessage(`Face image upload failed: ${ err.message }`);
                            setShowErrorModal(true);
                          } finally {
                            setImageUploading(false);
                          }
                        }}
                      />
                      <p className="text-muted mt-3 mb-0">
                        Position your face in the camera frame and click capture
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowCaptureModal(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) :
          type === "select" ? (
            <CommonSelect
              key={`${ key }-${ formData[key] || 'empty' }-${ isEdit ? studentFromState?.id : 'new' }`}
              className="select"
              options={
                userRole === 'Teacher' && key === 'schoolClassId'
                  ? classNames.map(className => {
                    const classId = groupedClassSections[className]?.[0]?.classId;
                    return { value: String(classId), label: className };
                  })
                  : userRole === 'Teacher' && key === 'classSectionId'
                    ? sectionsForSelectedClass.map((section: any) => ({
                      value: String(section.sectionId),
                      label: section.sectionName
                    }))
                    : dynamicOptionsMap[key] || []
              }
              defaultValue={
                formData[key] !== null && formData[key] !== "" && formData[key] !== undefined
                  ? {
                    value: String(formData[key]),
                    label:
                      userRole === 'Teacher' && key === 'schoolClassId'
                        ? (() => {
                          const matchedName = classNames.find(name =>
                            groupedClassSections[name]?.[0]?.classId === Number(formData[key])
                          );
                          return matchedName || String(formData[key]);
                        })()
                        : userRole === 'Teacher' && key === 'classSectionId'
                          ? (() => {
                            const selectedClassName = classNames.find(name =>
                              groupedClassSections[name]?.[0]?.classId === Number(formData.schoolClassId)
                            );
                            const section = groupedClassSections[selectedClassName || ""]?.find(
                              (sec: any) => sec.sectionId === Number(formData[key])
                            );
                            return section?.sectionName || String(formData[key]);
                          })()
                          : dynamicOptionsMap[key]?.find(opt => opt.value === String(formData[key]))?.label ||
                          String(formData[key])
                  }
                  : undefined
              }
              onChange={
                key === "schoolClassId"
                  ? handleClassChange
                  : key === "classSectionId"
                    ? handleSectionChange
                    : handleSelectChange(
                      key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null })
                    )
              }
              isDisabled={
                (key === "schoolClassId" && userRole !== 'Teacher' && loadingClasses) ||
                (key === "classSectionId" && userRole !== 'Teacher' && (!formData.schoolClassId || sectionOptions.length === 0)) ||
                (key === "academicYearId" && loadingAcademicYears)
              }
            />
          ) : type === "checkbox" ? (
            <CustomCheckbox
              id={key}
              className="form-check-input"
              checked={!!formData[key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null })]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange(
                  key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                  e.target.checked
                )
              }
            />
          ) : type === "email" ? (
            <CustomMail
              className="form-control"
              value={String(formData[key] || "")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange(
                  key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                  e.target.value
                )
              }
              placeholder={label}
            />
          ) : type === "radio" ? (
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <CustomRadio
                id={`${ key }-yes`}
                name={key}
                checked={
                  formData[
                  key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null })
                  ] === true
                }
                value={true}
                label="Yes"
                onChange={() =>
                  handleInputChange(
                    key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                    true
                  )
                }
              />
              <CustomRadio
                id={`${ key }-no`}
                name={key}
                checked={
                  formData[
                  key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null })
                  ] === false
                }
                value={false}
                label="No"
                onChange={() =>
                  handleInputChange(
                    key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                    false
                  )
                }
              />
            </div>
          ) : type === "textarea" || key === "address" ? (
            <CustomTextArea
              id={key}
              className="form-control w-100"
              placeholder={label}
              value={String(formData[key] || "")}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleInputChange(
                  key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                  e.target.value
                )
              }
            />
          ) : type === "date" ? (
            <CustomDate
              id={key}
              className="form-control"
              value={
                formData[key] != null
                  ? String(formData[key])
                  : ""
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange(
                  key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                  e.target.value || null
                )
              }
            />
          ) : (
            <CustomInput
              id={key}
              className="form-control"
              placeholder={label}
              value={String(formData[key] || "")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange(
                  key as keyof (Omit<Student, "id"> & { physicalChallengeDetails?: string | null }),
                  e.target.value
                )
              }
            />
          )}
        {errors[key] && (
          <div className="text-danger small">{errors[key]}</div>
        )}
      </>
    );
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3>{isEdit ? "Edit" : "Add"} Student</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={dashboardLink}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.studentList}>Students</Link>
                  </li>
                  <li className="breadcrumb-item active">{isEdit ? "Edit" : "Add"} Student</li>
                </ol>
              </nav>
            </div>
          </div>

          <form onSubmit={handleSubmit} key={isEdit ? studentFromState?.id : 'new'}>
            {Object.entries(studentinputs[0]).map(([sectionName, fields]) => (
              <div className="card mb-4" key={sectionName}>
                <div className="card-header bg-light">
                  <h4 className="text-dark">
                    {sectionName.replace(/([A-Z])/g, " $1")}
                  </h4>
                </div>
                <div className="card-body pb-1">
                  {sectionName === "FamilyDetails" ? (
                    // Custom layout for Family Details
                    <>
                      {/* Father's Information Row */}
                      <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="mb-3">
                            {renderFormField(fields.find((f: any) => f.key === "fatherName"))}
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="mb-3">
                            {renderFormField(fields.find((f: any) => f.key === "fatherAadhar"))}
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="mb-3">
                            {renderFormField(fields.find((f: any) => f.key === "primaryContactNo"))}
                          </div>
                        </div>
                      </div>

                      {/* Mother's Information Row */}
                      <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="mb-3">
                            {renderFormField(fields.find((f: any) => f.key === "motherName"))}
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="mb-3">
                            {renderFormField(fields.find((f: any) => f.key === "motherAadhar"))}
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="mb-3">
                            {renderFormField(fields.find((f: any) => f.key === "secondaryContactNo"))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : sectionName === "AddressDetails" ? (
                    // Full width for Address
                    <div className="row">
                      <div className="col-12">
                        <div className="mb-3">
                          {renderFormField(fields[0])}
                        </div>
                      </div>
                    </div>
                  ) : sectionName === "MedicalDetails" ? (
                    // Custom layout for Medical Details
                    <div className="row">
                      <div className="col-lg-6 col-md-8 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Physically Challenged
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <div className="d-flex align-items-center gap-3 flex-wrap">
                            <CustomRadio
                              id="physicallyChallenged-yes"
                              name="physicallyChallenged"
                              checked={formData.physicallyChallenged === true}
                              value={true}
                              label="Yes"
                              onChange={() =>
                                handleInputChange("physicallyChallenged", true)
                              }
                            />
                            <CustomRadio
                              id="physicallyChallenged-no"
                              name="physicallyChallenged"
                              checked={formData.physicallyChallenged === false}
                              value={false}
                              label="No"
                              onChange={() =>
                                handleInputChange("physicallyChallenged", false)
                              }
                            />
                          </div>
                          {errors.physicallyChallenged && (
                            <div className="text-danger small">{errors.physicallyChallenged}</div>
                          )}
                        </div>
                      </div>

                      {/* Physical Challenge Details - Shows in same row when Yes is selected */}
                      {formData.physicallyChallenged === true && (
                        <div className="col-lg-3 col-md-4 col-12">
                          <div className="mb-3">
                            <label htmlFor="physicalChallengeDetails" className="form-label">
                              Physical Challenge Details <span className="text-danger">*</span>
                            </label>
                            <CustomInput
                              id="physicalChallengeDetails"
                              className="form-control"
                              placeholder="Specify challenge details"
                              value={String(formData.physicalChallengeDetails || "")}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleInputChange("physicalChallengeDetails", e.target.value)
                              }
                            />
                            {errors.physicalChallengeDetails && (
                              <div className="text-danger small">{errors.physicalChallengeDetails}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Default layout for other sections
                    <div className="row row-cols-xxl-5 row-cols-md-6">
                      {fields.map(({ label, type, key, mandatory }: any) => {
                        return (
                          <div className="col-xxl col-xl-3 col-md-6" key={key}>
                            <div className="mb-3">
                              {renderFormField({ label, type, key, mandatory })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="text-end">
              <button
                type="button"
                className="btn btn-light me-3"
                onClick={() => {
                  navigate(all_routes.studentGrid);
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || imageUploading}>
                {loading
                  ? isEdit
                    ? "Saving..."
                    : "Adding..."
                  : isEdit
                    ? "Save Changes"
                    : "Add Student"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={handleCloseErrorModal}
      />

      <SuccessModal
        show={showSuccessModal}
        onClose={handleCloseSuccessModal}
        message={successMessage}
      />
    </div>
  );
};

export default AddStudent;